import {
  mapArtistGenreType,
  mapOrganizationUnitType,
  PermissionNameUnionType,
  useFindAllOrganizationUnitsSuspense,
  useFindArtistGenresSuspense,
} from '@api';
import { ArtistFormFields } from '@common';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { Button, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  AddArtistFormSchemaType,
  useAddArtistForm,
  useSubmitNewArtist,
} from './hooks';

export const AddArtist: React.FC = () => {
  const { t } = useTranslation('artists');
  const [opened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const form = useAddArtistForm();
  const { submitNewArtist } = useSubmitNewArtist();
  const { hasPermission } = usePermissionContext<PermissionNameUnionType>();
  const { data: artistGenres } = useFindArtistGenresSuspense({
    query: {
      select: (data) => data.data.map(mapArtistGenreType),
    },
  });
  const { data: organizationUnits } = useFindAllOrganizationUnitsSuspense({
    query: {
      select: (data) => data.data.map(mapOrganizationUnitType),
    },
  });

  const onClose = () => {
    form.reset();
    closeModal();
  };

  const handleSubmit = async (data: AddArtistFormSchemaType) => {
    await submitNewArtist(data);
    onClose();
  };

  const userCanEditOrganizationUnit = hasPermission({
    permission: 'ORGANIZATION_UNIT',
    action: 'WRITE',
  });

  const userCanEditAbbreviation = hasPermission({
    permission: 'ARTISTS_ABBREVIATION',
    action: 'WRITE',
  });

  return (
    <>
      <Button
        size="xs"
        color="blue"
        onClick={openModal}
        aria-label={t('ADD_ARTIST')}
      >
        <FontAwesomeIcon icon={faPlus} />
      </Button>
      <Modal
        opened={opened}
        onClose={onClose}
        title={t('ADD_ARTIST')}
        size="md"
        closeOnClickOutside={false}
      >
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <ArtistFormFields
              artistGenres={artistGenres}
              organizationUnits={organizationUnits}
              canEditOrganizationUnit={userCanEditOrganizationUnit}
              canEditAbbreviation={userCanEditAbbreviation}
              organizationUnitFieldName="defaultOrganizationUnit"
              onClose={onClose}
            />
          </form>
        </FormProvider>
      </Modal>
    </>
  );
};
